import { Col, Row } from 'antd';
import React, { useContext, useEffect, useMemo } from 'react';
import styles from './newOrganisationModal.module.scss';
import {
  getMainParentTabData,
  handleCompanyTypeChange,
} from './addNewOrganisation.utls';
import { NewOrganisationModalEnum } from 'enums/organisationMapEnums';
import InputField from 'shared/components/InputField';
import COSelect from 'shared/components/COSelect';
import { getNewOrganisationFields } from './addNewOrganisationFields';
import { MetaData } from 'models/MetaData/metaData.model';
import { Organization } from 'models/CompanySetup/organization.model';
import { useFormikContext } from 'formik';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { allowOnlyPercentages } from 'shared/utils/convertFormData';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { organizationalBoundary } from 'views/CompanySetup/companySetupConstants';

const {
  ORGANISATIONAL_BOUNDARY_FIELD_NAME,
  SECTOR_FIELD_NAME,
  COMPANY_TYPE_FIELD_NAME,
} = NewOrganisationModalEnum;

const OrganisationFormFields = (props: {
  activeTab: NewOrganisationModalEnum;
  isMainParent?: boolean;
  isOrganisationDetailsTab: boolean;
  sizeMetaData: MetaData[];
  sectorMetaData: MetaData[];
  companyTypesMeta: MetaData[];
  fetchSector: (companyTypeId: number) => Promise<void>;
  data?: OrganizationDetails;
}) => {
  const { setFieldValue, values } = useFormikContext<Organization>();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { setFieldValue: editOrgSetField, values: editOrgValues } =
    useFormikContext<OrganizationDetails>();
  const {
    isMainParent,
    activeTab,
    sizeMetaData,
    sectorMetaData,
    companyTypesMeta,
    isOrganisationDetailsTab,
    fetchSector,
    data,
  } = props;

  useEffect(() => {
    if (!!data?.organization?.id && data.organization.companyType?.id)
      fetchSector(data.organization.companyType?.id);
  }, [data]);

  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const mainParentTabs = useMemo(() => {
    return getMainParentTabData(activeTab, isMainParent);
  }, [activeTab, isMainParent]);

  const {
    ORGANISATION_BOUNDARY,
    ORGANISATION_NAME,
    DROPDOWNS,
    REPORTING_DETAILS,
    REPORTING_DETAIL_BASELINE_THRESHOLD,
  } = useMemo(() => {
    return getNewOrganisationFields({
      sizeMetaData,
      sectorMetaData,
      companyTypesMeta,
      isMainParent,
      data,
      handleToolTipOpen,
    });
  }, [sizeMetaData, sectorMetaData, companyTypesMeta, isMainParent]);

  return (
    <Row className={styles.confirmationContent}>
      <Col span={24} className={styles.modalTitle}>
        {`${
          data?.organization?.id
            ? 'Edit'
            : `New${isMainParent ? ' Parent' : ''}`
        } Organization`}
      </Col>
      {mainParentTabs}
      <Col span={24} className={styles.scrollable}>
        <Row>
          {(!isMainParent || isOrganisationDetailsTab) && (
            <>
              <Col span={24} className={styles.fields}>
                <InputField
                  name={ORGANISATION_NAME.NAME}
                  placeholderstring={ORGANISATION_NAME.PLACEHOLDER}
                  label={ORGANISATION_NAME.LABEL}
                  info={ORGANISATION_NAME?.INFO}
                  infoClick={ORGANISATION_NAME?.INFO_HANDLER}
                />
              </Col>
              {DROPDOWNS.filter(item => !item.HIDDEN).map((item, index) => (
                <Col
                  span={24}
                  className={styles.fields}
                  key={`dropdown${index}`}>
                  <COSelect
                    name={item.NAME}
                    placeholderstring={item.PLACEHOLDER}
                    label={item.LABEL}
                    options={item.OPTIONS}
                    onChange={
                      item.key === COMPANY_TYPE_FIELD_NAME
                        ? handleCompanyTypeChange(
                            data ? editOrgSetField : setFieldValue,
                            fetchSector,
                            data
                          )
                        : undefined
                    }
                    info={item?.INFO}
                    allowClear={item.key !== ORGANISATIONAL_BOUNDARY_FIELD_NAME}
                    infoClick={item?.INFO_HANDLER}
                  />
                </Col>
              ))}
              {ORGANISATION_BOUNDARY.HIDDEN ? (
                ''
              ) : (
                <Col span={24} className={styles.fields}>
                  <InputField
                    name={ORGANISATION_BOUNDARY.NAME}
                    placeholderstring={ORGANISATION_BOUNDARY.PLACEHOLDER}
                    label={ORGANISATION_BOUNDARY.LABEL}
                    onKeyDown={allowOnlyPercentages}
                    info={ORGANISATION_BOUNDARY?.INFO}
                    infoClick={ORGANISATION_BOUNDARY?.INFO_HANDLER}
                  />
                </Col>
              )}
            </>
          )}
          {isMainParent && !isOrganisationDetailsTab && (
            <>
              {REPORTING_DETAILS.map((item, index) => (
                <Col
                  span={24}
                  className={styles.fields}
                  key={`reportingDetailsDropdown${index}`}>
                  <COSelect
                    name={item.NAME}
                    placeholderstring={item.PLACEHOLDER}
                    label={item.LABEL}
                    disabled={item.DISABLED}
                    options={item.OPTIONS}
                    info={item?.INFO}
                    infoClick={item?.INFO_HANDLER}
                  />
                </Col>
              ))}
              <Col span={24} className={styles.fields}>
                <InputField
                  name={REPORTING_DETAIL_BASELINE_THRESHOLD.NAME}
                  placeholderstring={
                    REPORTING_DETAIL_BASELINE_THRESHOLD.PLACEHOLDER
                  }
                  label={REPORTING_DETAIL_BASELINE_THRESHOLD.LABEL}
                  onKeyDown={allowOnlyPercentages}
                  info={REPORTING_DETAIL_BASELINE_THRESHOLD.INFO}
                  infoClick={REPORTING_DETAIL_BASELINE_THRESHOLD?.INFO_HANDLER}
                />
              </Col>
              <Col span={24} className={styles.emptyContainer}></Col>
            </>
          )}
        </Row>
      </Col>
    </Row>
  );
};

export default OrganisationFormFields;
